import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { DatePicker, message } from 'antd';
import Modal from 'common/components/modal';
import formatPrice from '~helpers/currency';
import {
  CheckCircle,
  LandlordRelatedInformation,
  ProcessArrow,
  CheckCircleSuccess,
  ArrowRight,
} from '~components/svgs';
import { CloseCircleOutlined } from '@ant-design/icons';
import { MUTATION_DS_UpdateLandlord_ListingPlan, QUERY_LEADGEN_ORDERS } from '../queries';
import { upperCase } from 'lodash';
import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import csvExporter from '~helpers/csvExporter';
import lookup from 'country-code-lookup';
import { Hosts } from '~helpers/environments';

function getCountryFromNationality(nationality) {
  if (!nationality || nationality === 'ZZZ') {
    return '-';
  }
  const countryInfo = lookup.byIso(nationality);
  return countryInfo?.country || '-';
}
const generatePropertyUrl = property => {
  const path = `${
    property.city && property.city.country && property.city.country.slug
      ? property.city.country.slug
      : ''
  }/${property.city && property.city.slug ? property.city.slug : ''}/p/${property.slug}`;

  return `${Hosts.propertyUrl}${path}`;
};
const uesStyles = makeStyles(
  theme => ({
    wrapper: {
      '& .ant-modal-header': {
        padding: '32px 32px 24px 32px',
      },
      '& .ant-modal-body': {
        padding: '0 32px',
      },

      '& .ant-form-item-label': {
        lineHeight: '22px',
        marginBottom: 8,
      },
    },

    bodyRegular: {
      fontSize: '14px',
      fontWeight: 400,
      color: 'rgba(0, 0, 0, 0.85)',
      lineHeight: '22px',
    },
  }),
  { name: 'SignmeUp-Dialog' },
);
const { RangePicker } = DatePicker;

function Index({ setOpen, open }) {
  const classes = uesStyles();
  const [selectedDates, setSelectedDates] = React.useState([]);

  // createdAfter: $createdAfter;
  // createdBefore: $createdBefore;

  const [queryLeadOrders, { loading: leadOrdersLoading }] = useLazyQuery(QUERY_LEADGEN_ORDERS, {
    fetchPolicy: 'no-cache',
    onCompleted: result => {
      const headers = {
        bookingNumber: 'Booking Number',
        property: 'Property',
        desiredMoveInDate: 'Desired Move-in Date',
        desiredMoveOutDate: 'Desired Move-out Date',
        tenancyLength: 'Tenancy Length',
        createDate: 'Create Date',
        legalFirstName: 'Legal First Name',
        legalLastName: 'Legal Last Name',
        email: 'Email',
        phone: 'Phone',
        identificationType: 'Identification Type',
        idNumber: 'ID Number',
        gender: 'Gender',
        dateOfBirth: 'Date of Birth',
        theSchoolIAmAboutToAttend: 'The School I am About to Attend',
        degree: 'Degree',
        specialRequirements: 'Special Requirements',
        issueCountry: 'Nationality/Region',
        notes: 'Notes',
        updateTime: 'Update Time',
        propertyName: 'Property Name',
        location: 'Location',
        entryToWebsiteLink: 'Entry to Website Link',
        roomName: 'Room Name (room type)',
        moveInAndMoveOutDate: 'Move-in & Move-out Date',
        totalPrice: 'Total Price',
      };

      let data = result.viewer.company.orders.edges.map(t => {
        let node = t.node;
        let keyValuePairs = {};
        t.node?.customFieldValues.forEach(item => {
          keyValuePairs[item.customFieldInternalName] = item.value;
        });
        let notes =
          node?.orderNotes?.map((m, idx) => `${idx + 1}${'.' + m.note}`).join('\n') ?? '-';
        return {
          bookingNumber: node?.referenceId ?? '-',
          property: node?.propertyName ?? '-',
          desiredMoveInDate: node?.moveInDate ?? '-',
          desiredMoveOutDate: node?.moveOutDate ?? '-',
          tenancyLength: node?.tenancyLength?.days + ' days',
          createDate: node?.createdAt ?? '-',
          legalFirstName: node?.student?.passportFirstName ?? '-',
          legalLastName: node?.student?.passportLastName ?? '-',
          email: node?.student?.emailAddress ?? '-',
          phone: node?.student?.phoneNumber ?? '-',
          identificationType: keyValuePairs?.identification_type ?? '-',
          idNumber: keyValuePairs?.id_number ?? '-',
          gender: node?.student?.gender ?? '-',
          dateOfBirth: node?.student?.dateOfBirth ?? '-',
          theSchoolIAmAboutToAttend: keyValuePairs?.the_school_i_am_about_to_attend ?? '-',
          degree: keyValuePairs?.degree ?? '-',
          specialRequirements: keyValuePairs?.special_requirement ?? '-',
          issueCountry: getCountryFromNationality(keyValuePairs?.nationality_region),
          notes: notes,
          updateTime: node?.student?.updatedAt ?? '-',
          propertyName: node?.propertyName ?? '-',
          location: `${node?.property?.city?.name}, ${node?.property?.city?.country?.name}`,
          entryToWebsiteLink: generatePropertyUrl(node?.property),
          roomName: node?.unitTypeName,
          moveInAndMoveOutDate:
            moment(node?.moveInDate).format('DD/MM/YYYY') +
            ' - ' +
            moment(node?.moveOutDate).format('DD/MM/YYYY'),
          totalPrice: formatPrice(node?.totalPrice, node?.currency) ?? '-',
        };
      });

      if (data?.length > 0) {
        csvExporter(headers, data);
      } else {
        message.error('No data to export');
      }
    },
    onError: e => {
      message.error(e.toString());
    },
  });

  const disabledDate = (current, selectedDates) => {
    // const startDate = moment('2022-01-01', 'YYYY-MM-DD');

    // if (current && current.isBefore(startDate)) {
    //   return true;
    // }

    // 如果已经有选择的日期，判断是否在90天内
    if (selectedDates.length > 0) {
      const [firstPicked, secondPicked] = selectedDates;
      if (firstPicked) {
        const minDate = firstPicked.clone().subtract(90, 'days');
        const maxDate = firstPicked.clone().add(90, 'days');
        return current && (current.isBefore(minDate) || current.isAfter(maxDate));
      }

      if (secondPicked && !firstPicked) {
        const minDate = secondPicked.clone().subtract(90, 'days');
        const maxDate = secondPicked.clone().add(90, 'days');
        return current && (current.isBefore(minDate) || current.isAfter(maxDate));
      }
    }

    return false;
  };

  return (
    <Modal
      width={446}
      visible={open}
      title={'Export'}
      okText={'Confirm'}
      cancelText="Cancel"
      wrapClassName={classes.wrapper}
      onCancel={() => {
        setOpen(false);
      }}
      onOk={() => {
        // setConfirmOpen(true);
        if (selectedDates?.length === 2) {
          if (Math.abs(selectedDates[0].diff(selectedDates[1], 'days')) <= 90) {
            queryLeadOrders({
              variables: {
                pageSize: 99999,
                createdAfter: moment(selectedDates[0]).format('YYYY-MM-DD'),
                createdBefore: moment(selectedDates[1]).format('YYYY-MM-DD'),
              },
            });
          } else {
            message.error('Please select a date range less than 90 days');
          }
        }
      }}
      okButtonProps={{
        loading: leadOrdersLoading,
        disabled: selectedDates?.length !== 2,
      }}
      destroyOnClose={true}
    >
      <div className="leadgen_export_content">
        <div className={classes.bodyRegular}>Date Range:</div>
        <RangePicker
          value={selectedDates}
          style={{ width: '100%', height: '32px', borderRadius: '2px', margin: '8px' }}
          format="ll"
          showToday={false}
          onChange={dates => setSelectedDates(dates)}
          disabledDate={current => disabledDate(current, selectedDates)}
        />
      </div>
    </Modal>
  );
}

export default Index;
